import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { declinePatient } from "@/api/Patients";
import { FilledButton } from "@medoma/components/Button/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import Form from "@/components/Form/Form";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect } from "react";
import Checkbox from "@/components/Checkbox/Checkbox";
import styles from "./Decline.module.scss";
import type { IExistingPatient } from "@medoma/models/patients";
import { getPatientFullName } from "@medoma/models/patients";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import InputField from "@medoma/components/InputField/InputField";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import type { IActivity } from "@/api/Activities";
import { patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions } from "@/api/Activities";
import { ActivitiesToCloseOrReactivate } from "../CareEpisode/ActivitiesToCloseOrReactivate";
import { Text } from "@medoma/components/Text/Text";

type IDeclineFormData = {
  cancelledScheduledActivities: boolean;
  declinedAt: string;
  timeOfDecline: "today" | "earlier";
  activityIds: string[];
};

const Decline = ({
  patient,
  onDeclined,
}: {
  patient: IExistingPatient;
  onDeclined: () => void;
}) => {
  const {
    data: activities,
    isPending,
    isError,
  } = useQuery(
    patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions({
      patientId: patient.id,
    }),
  );

  if (isPending) {
    return <Loading message={t`Hämtar information för avslag`} />;
  }

  return (
    <DeclineForm
      patient={patient}
      activities={activities ?? []}
      errorFetchingActivities={isError}
      onDeclined={onDeclined}
    />
  );
};

const DeclineForm = ({
  patient,
  activities,
  errorFetchingActivities,
  onDeclined,
}: {
  patient: IExistingPatient;
  activities: IActivity[];
  errorFetchingActivities: boolean;
  onDeclined: () => void;
}) => {
  const patientName = getPatientFullName(patient.name);
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: declinePatientMutation, isPending: isDeclining } =
    useMutation({
      mutationFn: ({
        patientId,
        declinedAt,
        activitiesToClose,
      }: {
        patientId: string;
        declinedAt: Date;
        activitiesToClose?: string[];
      }) => declinePatient({ patientId, declinedAt, activitiesToClose }),
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att avslå ${patientName}.`} ${deducedError(error)}`,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        onDeclined();
      },
    });

  const methods = useForm<IDeclineFormData>({
    defaultValues: {
      cancelledScheduledActivities: false,
      declinedAt: machineFormat(new Date(), "yyyy-MM-dd"),
      timeOfDecline: "today",
      // One-time activities are left unchecked so the user opts in explicitly
      activityIds: activities
        .filter(({ recurring }) => recurring)
        .map(({ id }) => id),
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    resetField,
  } = methods;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const timeOfDecline = watch("timeOfDecline");

  useEffect(() => {
    if (timeOfDecline === "today") {
      resetField("declinedAt");
    }
  }, [timeOfDecline, resetField]);

  const validateAndSubmit = handleSubmit((formData) => {
    declinePatientMutation({
      patientId: patient.id,
      declinedAt: calendarDateSchema.decode(formData.declinedAt),
      activitiesToClose: formData.activityIds,
    });
  });

  return (
    <div className={styles.declineContent}>
      <FormProvider {...methods}>
        <Form onSubmit={validateAndSubmit}>
          <Text element="p">
            <Trans>
              OBS! Säkerställ först att det inte finns några olästa chattnotiser
              från patienten.
            </Trans>
          </Text>

          <RadioButtons
            legend={t`Datum för avslag`}
            errorMessage={errors.declinedAt?.message}
            orientation="horizontal"
          >
            <RadioButton
              label={{ text: t`Idag` }}
              {...register("timeOfDecline")}
              value={"today"}
              visualStyle="framed"
            />
            <RadioButton
              label={{ text: t`Tidigare datum` }}
              {...register("timeOfDecline")}
              value={"earlier"}
              visualStyle="framed"
            />
          </RadioButtons>
          {timeOfDecline === "earlier" ? (
            <InputField
              label={t`Tidigare datum`}
              {...register("declinedAt", { required: true })}
              type="date"
              errorMessage={errors.declinedAt?.message}
              showOptionalLabel={false}
            />
          ) : null}

          {errorFetchingActivities ? (
            <div className={styles.errorAndConfirmation}>
              <ErrorMessage
                message={t`Kunde inte hämta information om ej avslutade aktiviteter. Du måste därför själv ansvara för att säkerställa att alla aktiviteter är avslutade.`}
              />
              <Checkbox
                errorMessage={errors.cancelledScheduledActivities?.message}
                label={{ text: t`Patientens aktiviteter är avslutade` }}
                {...register("cancelledScheduledActivities", {
                  required: {
                    value: true,
                    message: t`Bekräfta att aktiviteterna har avslutats`,
                  },
                })}
              />
            </div>
          ) : activities.length > 0 ? (
            <ActivitiesToCloseOrReactivate
              activities={activities}
              legend={t`Välj de aktiviteter som ska avslutas vid avslag`}
            />
          ) : null}

          <FilledButton disabled={isDeclining} type="submit">
            <Trans>Avslå</Trans>
          </FilledButton>
        </Form>
      </FormProvider>
    </div>
  );
};

export { Decline };
